import React, { useState, useEffect } from 'react';
import Input from '../Form/Input';
import PhoneInput from '../Form/PhoneInput';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { inviteStudent, resetInviteSuccess, clearError } from '../../store/AdminSlices/studentSlice';
import { useToast } from '../Toast/ToastContainer';

interface AddStudentFormProps {
  onClose: () => void;
  onSubmit: (data: StudentFormData) => void;
}

export interface StudentFormData {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  focusArea: string;
  status: string;
  completedSessions: string;
  lastSession: string;
  additionalNotes: string;
}

const AddStudentForm: React.FC<AddStudentFormProps> = ({ onClose, onSubmit }) => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [invitedStudentName, setInvitedStudentName] = useState('');
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const dispatch = useAppDispatch();
  const { loading, error, inviteSuccess } = useAppSelector((state) => state.student);
  const { showToast } = useToast();

  const [formData, setFormData] = useState<StudentFormData>({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    focusArea: '',
    status: 'Not Started',
    completedSessions: '0',
    lastSession: '',
    additionalNotes: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Clear error on component mount
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Handle successful invite
  useEffect(() => {
    if (inviteSuccess) {
      const fullName = `${formData.firstName} ${formData.lastName}`.trim();
      setInvitedStudentName(fullName || 'Student');
      setShowSuccessModal(true);
      onSubmit(formData);
      dispatch(resetInviteSuccess());
    }
  }, [inviteSuccess, dispatch, onSubmit, formData]);

  // Handle error - only show if user has attempted submit
  useEffect(() => {
    if (error && hasAttemptedSubmit) {
      showToast('something went wrong try again', 'error');
    }
  }, [error, hasAttemptedSubmit, showToast]);
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    if (!formData.focusArea) newErrors.focusArea = 'Focus area is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);
    if (validateForm()) {
      // Dispatch the invite student action with API payload format
      await dispatch(
        inviteStudent({
          email: formData.email,
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone: formData.phone,
          focus_area: formData.focusArea,
          notes: formData.additionalNotes,
        })
      );
    }
  };

  const handleChange = (field: keyof StudentFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  return (
    <div className="">
      <div className="bg-white rounded-xl border border-[#F2F4F7] p-5 shadow-[0_4px_12px_0_rgba(249,250,251,1)]">
       

        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="mb-4 border-b border-gray-200 pb-4 text-sm font-medium text-gray-900">Basic Information</h3>
              <div className="space-y-4">
                <div className="max-w-md">
                  <Input
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={(value) => handleChange('email', value)}
                    placeholder="Enter Student's Email Address"
                    error={errors.email}
                    required
                    disabled={loading}
                  />
                  <p className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-400">
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Student will receive login credentials and all platform updates at this email.
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <Input
                    label="First Name"
                    type="text"
                    value={formData.firstName}
                    onChange={(value) => handleChange('firstName', value)}
                    placeholder="Enter Student's First Name"
                    error={errors.firstName}
                    required
                    disabled={loading}
                  />
                  <Input
                    label="Last Name"
                    type="text"
                    value={formData.lastName}
                    onChange={(value) => handleChange('lastName', value)}
                    placeholder="Enter Student's Last Name"
                    error={errors.lastName}
                    required
                    disabled={loading}
                  />
                  <PhoneInput
                    label="Phone"
                    value={formData.phone}
                    onChange={(value) => handleChange('phone', value)}
                    placeholder="Enter Student's Phone Number"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            {/* Coaching Information */}
            <div>
              <h3 className="mb-4 border-b border-gray-200 pb-4 text-sm font-medium text-gray-900">Coaching Information</h3>
              <div className="space-y-4">
                <div className="max-w-md">
                  <Input
                    label="Focus Area"
                    type="text"
                    value={formData.focusArea}
                    onChange={(value) => handleChange('focusArea', value)}
                    placeholder="Enter Area of Expertise"
                    error={errors.focusArea}
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            {/* Additional Notes */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Additional Notes
              </label>
              <textarea
                value={formData.additionalNotes}
                onChange={(e) => handleChange('additionalNotes', e.target.value)}
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Add any additional notes about the student"
                disabled={loading}
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="mt-6 flex gap-3">
            {(() => {
              const hasValues = formData.email && formData.firstName && formData.lastName && formData.focusArea;
              const buttonClass = hasValues
                ? 'flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:bg-gray-400 disabled:text-gray-200'
                : 'flex items-center gap-2 rounded-lg bg-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400';
              
              return (
                <button
                  type="submit"
                  className={buttonClass}
                  disabled={loading}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {loading ? 'Sending...' : 'Send Invitation'}
                </button>
              );
            })()}
            
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50">
              <svg
                className="h-7 w-7 text-emerald-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8m-2 9H5a2 2 0 01-2-2V7a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h2 className="text-center text-xl font-semibold text-gray-900">Student Invite Sent</h2>
            <p className="mt-2 text-center text-sm text-gray-500">
              You will be notified when <span className="font-semibold text-gray-900">{invitedStudentName}</span> accepts
              the invite and log in to his account
            </p>
            <button
              type="button"
              onClick={() => {
                setShowSuccessModal(false);
                onClose();
              }}
              className="mt-6 w-full rounded-xl bg-gray-900 py-3 text-sm font-semibold text-white hover:bg-gray-800"
            >
              Back to Students
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddStudentForm;
